import { RefObject, useEffect, useRef, useState } from "react";

import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { DragData } from "../types";

interface UseItemDraggableArgs {
    disabled: boolean;
    listId: string;
    index: number;
    isLockedItems: ReadonlyArray<boolean>;
}

interface UseItemDraggableResult {
    itemRef: RefObject<HTMLLIElement | null>;
    handleRef: RefObject<HTMLDivElement | null>;
    isDragging: boolean;
}

export function useItemDraggable({
    disabled,
    listId,
    index,
    isLockedItems,
}: UseItemDraggableArgs): UseItemDraggableResult {
    const itemRef = useRef<HTMLLIElement>(null);
    const handleRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Mirror isLockedItems so getInitialData (fired at drag start) sees the
    // latest snapshot without resubscribing on every render.
    const isLockedItemsRef = useRef(isLockedItems);
    // eslint-disable-next-line react-hooks/refs
    isLockedItemsRef.current = isLockedItems;

    // Register the draggable; cleanup runs on unmount or when deps change.
    useEffect(() => {
        const item = itemRef.current;
        const handle = handleRef.current;
        if (!item || !handle || disabled) {
            return;
        }
        return draggable({
            element: item,
            dragHandle: handle,
            getInitialData: (): Record<string, unknown> => {
                const data: DragData = {
                    listId,
                    index,
                    isLockedItems: isLockedItemsRef.current,
                };
                return { ...data };
            },
            onDragStart: () => setIsDragging(true),
            onDrop: () => setIsDragging(false),
        });
    }, [disabled, listId, index]);

    return { itemRef, handleRef, isDragging };
}
